/**
 * **Penjelajah menu tiga tingkat** — departemen → kategori → produk (Imron, 2026-08-02).
 *
 * ### ⭐ Kenapa navigasi, bukan cuma kotak cari
 * Cari menuntut kasir **tahu namanya**; kategori menuntut ia tahu **jenisnya**. Untuk katalog
 * ratusan produk, keduanya dibutuhkan — dan yang paling sering dipakai justru yang kedua, karena
 * tamu menyebut *"ada minuman apa?"*, bukan nama produk.
 *
 * ### ⚠️ Yang belum diklasifikasi TETAP TERLIHAT
 * Produk tanpa departemen/kategori masuk kelompok **"Lainnya"**, bukan disembunyikan. Menyembunyikan
 * berarti produk itu **lenyap dari layar kasir tanpa satu pun tanda**, dan yang mencarinya akan
 * menyimpulkan menunya belum diimpor. Guardrail #2 menghendaki klasifikasi struktural; selama
 * `department_id` belum wajib-isi, tugas layar adalah **membuat lubangnya terlihat**.
 *
 * ### ⭐ Departemen = RAIL tetap di kiri; kategori = chip di atas
 * Tingkat teratas yang berpindah tempat memaksa mata mencari sebelum jari bergerak. Rail memberi
 * tiap departemen satu tempat yang **tak berubah sepanjang shift**, dan jari menghafal tempat jauh
 * lebih cepat daripada mata membaca nama.
 *
 * ⚠️ Lencananya **inisial**, bukan emoji. Emoji menuntut pemetaan kode→gambar yang harus ditebak
 * per tenant ("SEAFOOD" itu 🦐 atau 🐟?), dan tebakan yang salah lebih mengganggu daripada tak ada
 * gambar sama sekali. Kalau kelak master departemen punya kolom ikonnya sendiri, di sinilah ia
 * masuk — sebagai DATA, bukan sebagai daftar `if` di layar.
 *
 * ### ⛔ Cari MENGALAHKAN navigasi
 * Begitu kotak cari terisi, tingkatnya diabaikan dan pencarian berlaku ke SELURUH katalog. Kasir
 * yang mengetik nama sudah tahu yang dicarinya; menyaringnya lagi menurut tab yang kebetulan aktif
 * hanya menghasilkan "menu tak ada" untuk barang yang jelas-jelas ada.
 */
import { useMemo, useRef } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import type { MenuItem } from '../api/client';

/** Kunci kelompok untuk yang belum diklasifikasi. ⚠️ Bukan string kosong — itu tak bisa dibedakan. */
const UNCLASSIFIED = ' none';

export interface MenuBrowserProps {
  readonly menu: readonly MenuItem[];
  readonly search: string;
  readonly department: string | null;
  readonly category: string | null;
  readonly onDepartment: (code: string | null) => void;
  readonly onCategory: (code: string | null) => void;
  readonly onPick: (item: MenuItem) => void;
  readonly formatPrice: (canonical: string) => string;
  /**
   * ⭐ Kode produk yang sedang HABIS — kartunya diredupkan & bertulis HABIS, **tak disembunyikan**.
   *
   * ⛔ Menyembunyikannya menghilangkan satu-satunya benda yang harus ditekan untuk menyalakannya
   * kembali; sesudah itu kasir harus menghafal apa saja yang ia matikan tadi.
   */
  readonly soldOut?: ReadonlySet<string>;
  /**
   * ⭐ **Tekan-tahan** kartu ⇒ tandai/cabut HABIS. `undefined` ⇒ gerakannya mati sama sekali.
   *
   * ⚠️ Sengaja tekan-tahan, bukan tombol kecil di sudut kartu: kisi menu ditekan ratusan kali per
   * shift dengan jari yang sedang buru-buru, dan sasaran kecil di dalam sasaran besar akan tertekan
   * tanpa disengaja. Tekan-tahan mustahil terjadi karena buru-buru.
   */
  readonly onHold?: (item: MenuItem) => void;
}

interface Group {
  readonly code: string;
  readonly name: string;
}

/**
 * Rona stabil per departemen, diturunkan dari KODE-nya.
 *
 * ⭐ Warna di sini berarti **identitas**, bukan keadaan — dan karena ia dihitung dari kode, ia
 * sama di tiap perangkat, tiap shift, tanpa satu pun baris setelan. Jari menghafal warna lebih
 * cepat daripada mata membaca nama.
 *
 * ⛔ Sengaja cuma TITIK, bukan tombol berwarna penuh seperti referensi. Sembilan warna jenuh akan
 * berebut perhatian dengan warna KEADAAN — merah "kurang bayar" dan hijau "lunas" harus tetap
 * jadi dua hal paling mencolok di layar ini.
 */
function hue(code: string): number {
  let h = 0;
  for (const ch of code) h = (h * 31 + ch.charCodeAt(0)) % 150;
  /*
   * ⛔ Dikurung di 180°–330° (biru → ungu → magenta): MERAH, AMBER, dan HIJAU tak boleh terpakai
   * sebagai identitas. Ketiganya sudah punya arti tetap di layar ini — kurang bayar, perlu
   * ditinjau, lunas — dan departemen "Bar" yang kebetulan berlencana merah mengajari mata bahwa
   * merah kadang tak berarti apa-apa. Sesudah itu merah yang sungguhan pun tak dibaca.
   */
  return 180 + h;
}

/** Inisial untuk lencana rail: dua huruf pertama dari kata-kata namanya. */
function initials(name: string): string {
  const kata = name.trim().split(/\s+/).filter((w) => w !== '');
  if (kata.length === 0) return '?';
  if (kata.length === 1) return (kata[0] ?? '').slice(0, 2).toUpperCase();
  return `${(kata[0] ?? '').charAt(0)}${(kata[1] ?? '').charAt(0)}`.toUpperCase();
}

/** Kelompok unik, urutannya mengikuti urutan katalog dari server — itu urutan yang dikurasi. */
function groupsOf(
  items: readonly MenuItem[],
  code: (i: MenuItem) => string | null | undefined,
  name: (i: MenuItem) => string | null | undefined,
  fallback: string,
): Group[] {
  const out: Group[] = [];
  for (const i of items) {
    const c = code(i) ?? UNCLASSIFIED;
    if (out.some((g) => g.code === c)) continue;
    out.push({ code: c, name: c === UNCLASSIFIED ? fallback : (name(i) ?? c) });
  }
  return out;
}

/** Lama tekan sebelum dianggap "tekan-tahan" (ms). Cukup lama agar mustahil tak sengaja. */
const HOLD_MS = 550;

export function MenuBrowser(p: MenuBrowserProps) {
  const { t } = useTranslation();

  /*
   * ⚠️ `fired` ada supaya ketukan yang menyusul SESUDAH tekan-tahan tidak ikut menambah baris.
   * Tanpa itu, menandai habis juga memasukkan barangnya ke bill — persis kebalikan maksudnya.
   */
  const hold = useRef<{ timer: ReturnType<typeof setTimeout>; fired: boolean } | null>(null);

  const holdStart = (m: MenuItem) => {
    if (p.onHold === undefined) return;
    holdCancel();
    const st = { timer: setTimeout(() => { st.fired = true; p.onHold?.(m); }, HOLD_MS), fired: false };
    hold.current = st;
  };
  const holdCancel = () => {
    if (hold.current !== null) clearTimeout(hold.current.timer);
  };
  const tap = (m: MenuItem) => {
    const fired = hold.current?.fired === true;
    holdCancel();
    hold.current = null;
    if (fired) return;
    p.onPick(m);
  };
  const searching = p.search.trim() !== '';

  const departments = useMemo(
    () => groupsOf(p.menu, (i) => i.departmentCode, (i) => i.departmentName, t('cashier.other')),
    [p.menu, t],
  );

  /** Produk di departemen aktif — dasar bagi daftar kategori DAN grid. */
  const inDepartment = useMemo(
    () =>
      p.department === null
        ? p.menu
        : p.menu.filter((i) => (i.departmentCode ?? UNCLASSIFIED) === p.department),
    [p.menu, p.department],
  );

  const categories = useMemo(
    () => groupsOf(inDepartment, (i) => i.categoryCode, (i) => i.categoryName, t('cashier.other')),
    [inDepartment, t],
  );

  const visible = useMemo(() => {
    if (searching) {
      // ⛔ Cari menembus SELURUH katalog — lihat alasannya di kepala berkas.
      const q = p.search.trim().toLowerCase();
      return p.menu.filter((m) => m.name.toLowerCase().includes(q));
    }
    return p.category === null
      ? inDepartment
      : inDepartment.filter((i) => (i.categoryCode ?? UNCLASSIFIED) === p.category);
  }, [inDepartment, p.category, p.menu, p.search, searching]);

  /**
   * Kisi dipecah per KATEGORI saat kategori belum dipilih; selain itu satu seksi tanpa judul.
   * ⚠️ Urutannya mengikuti urutan katalog dari server — itu urutan yang dikurasi pemilik.
   */
  const sections = useMemo(() => {
    if (searching || p.category !== null) {
      return [{ code: '', name: '', items: visible }];
    }
    const out: { code: string; name: string; items: MenuItem[] }[] = [];
    for (const m of visible) {
      const c = m.categoryCode ?? UNCLASSIFIED;
      let sec = out.find((x) => x.code === c);
      if (sec === undefined) {
        sec = { code: c, name: c === UNCLASSIFIED ? t('cashier.other') : (m.categoryName ?? c), items: [] };
        out.push(sec);
      }
      sec.items.push(m);
    }
    return out;
  }, [visible, searching, p.category, t]);

  return (
    <>
      {/* ── tingkat 1: departemen — rail tetap di kiri ───────────────── */}
      {!searching && departments.length > 1 && (
        <nav className="dept-rail" aria-label={t('cashier.department')}>
          <button
            type="button"
            className={p.department === null ? 'on' : undefined}
            data-testid="dept-all"
            onClick={() => { p.onDepartment(null); p.onCategory(null); }}
          >
            <span className="dept-badge" aria-hidden="true">★</span>
            {t('cashier.all')}
          </button>
          {departments.map((d) => (
            <button
              key={d.code}
              type="button"
              className={p.department === d.code ? 'on' : undefined}
              data-testid={`dept-${d.code === UNCLASSIFIED ? 'none' : d.code}`}
              /* ⚠️ Ganti departemen MENGOSONGKAN kategori: kategori lama hampir pasti tak ada di
                 departemen baru, dan grid kosong tanpa sebab terbaca sebagai menu yang hilang. */
              onClick={() => { p.onDepartment(d.code); p.onCategory(null); }}
            >
              <span
                className="dept-badge"
                aria-hidden="true"
                style={{ '--dept-hue': hue(d.code) } as CSSProperties}
              >
                {initials(d.name)}
              </span>
              {d.name}
            </button>
          ))}
        </nav>
      )}

      <div className="menu-main">
      {/* ── tingkat 2: kategori ───────────────────────────────────────── */}
      {!searching && categories.length > 1 && (
        <nav className="level level-category" aria-label={t('cashier.category')}>
          <button
            type="button"
            className={p.category === null ? 'level-chip on' : 'level-chip'}
            data-testid="cat-all"
            onClick={() => { p.onCategory(null); }}
          >
            {t('cashier.all')}
          </button>
          {categories.map((c) => (
            <button
              key={c.code}
              type="button"
              className={p.category === c.code ? 'level-chip on' : 'level-chip'}
              data-testid={`cat-${c.code === UNCLASSIFIED ? 'none' : c.code}`}
              onClick={() => { p.onCategory(c.code); }}
            >
              {c.name}
            </button>
          ))}
        </nav>
      )}

      {/* ── tingkat 3: produk ─────────────────────────────────────────── */}
      {/*
        ⭐ **Berseksi + berhitung** saat kategori belum dipilih (dari referensi rail bersekat).
        "Makanan Utama · 12 item" menjawab "menunya cuma segini?" sebelum ditanyakan — dan
        pertanyaan itu selalu muncul di outlet yang katalognya baru diimpor separuh.
        ⚠️ Saat kategori SUDAH dipilih (atau sedang mencari), judulnya mubazir: kisi itu sudah
        satu seksi. Judul yang mengulang keadaan yang sudah jelas cuma memakan tinggi layar.
      */}
      {sections.map((sec) => (
        <section key={sec.code} className="menu-section">
          {/*
            ⭐⭐ **Judul kategori tampil walau cuma SATU** (Imron, 2026-08-06: *"saya pilih
            department BEV, tetapi kategorinya tidak muncul"*).

            ⛔ Dulu tiga aturan "sembunyikan bila cuma satu" bertumpuk — rail departemen, chip
            kategori, DAN judul seksi. Pada departemen ber-satu-kategori akibatnya: klasifikasinya
            **hilang sama sekali dari layar**, dan tak ada cara bagi siapa pun untuk tahu apakah
            menunya sudah dikategorikan atau belum. Berkas ini sendiri menyatakan tugas layar
            adalah **membuat lubangnya terlihat**; menyembunyikan strukturnya justru kebalikannya.

            ⚠️ Chip kategori TETAP disembunyikan saat cuma satu — di sana memang tak ada yang bisa
            dipilih ("Semua" dan satu-satunya kategori menampilkan isi yang sama persis). Judul
            MENYATAKAN, chip MEMILIH; cuma yang kedua yang jadi mubazir.
          */}
          {sec.name !== '' && (
            <h2 className="menu-section-head">
              {sec.name}
              <span className="menu-section-count">
                {t('cashier.itemCount', { count: sec.items.length })}
              </span>
            </h2>
          )}
          <div className="menu-grid">
            {sec.items.map((m) => {
              const habis = p.soldOut?.has(m.code) === true;
              return (
                <button
                  key={m.code}
                  type="button"
                  className={habis ? 'menu-card habis' : 'menu-card'}
                  data-testid={`menu-${m.code}`}
                  data-soldout={habis ? 'yes' : undefined}
                  onClick={() => { tap(m); }}
                  onPointerDown={() => { holdStart(m); }}
                  onPointerUp={holdCancel}
                  onPointerLeave={holdCancel}
                  onPointerCancel={holdCancel}
                  /* ⛔ Menu konteks bawaan browser muncul saat tekan-tahan di Android dan menutupi
                     dialognya sendiri. */
                  onContextMenu={(e) => { e.preventDefault(); }}
                >
                  <span className="menu-name">{m.name}</span>
                  {habis
                    ? <span className="menu-soldout">{t('cashier.soldOut.badge')}</span>
                    : <span className="menu-price">{p.formatPrice(m.price)}</span>}
                </button>
              );
            })}
          </div>
        </section>
      ))}

      {/* ⚠️ Kosong karena SARINGAN ≠ kosong karena katalog gagal dimuat. Dua sebab, dua pesan. */}
      {visible.length === 0 && p.menu.length > 0 && (
        <p className="empty" data-testid="menu-filtered-empty">{t('cashier.noMatch')}</p>
      )}
      </div>
    </>
  );
}
